/**
 * Authentication-screen design tokens.
 *
 * The sign-in and account-recovery screens are the only surfaces in the portal
 * that render *before* a user's saved theme is known, so they deliberately do
 * not follow `Setting → Theme Color`: an installation's configured accent is
 * loaded from `localStorage` at the end of the previous session and would make
 * the first screen of the product look different on every machine. These screens
 * are fixed to the GT Brokers brand instead.
 *
 * Every value below is derived from the existing system rather than invented —
 * `navy`, `blue`, `cyan`, `border`, `textStrong`, `textMuted` and `surfaceSunken`
 * are the same hexes `theme/appcolors.ts` uses as its GT defaults. Geometry and
 * motion still come from `theme/tokens.ts`.
 */

export const authTokens = {
  /* ------------------------------------------------------------ surfaces */
  /** Left branding panel — the darkest surface in the product. */
  navy: "#0B1B2E",
  /** Bottom-right of the branding gradient. */
  navyDeep: "#071322",
  /** Top-left of the branding gradient. */
  navyRaised: "#122539",
  /** Right panel page background. */
  pageBg: "#F7F9FC",
  /** The card itself. */
  surface: "#FFFFFF",

  /* --------------------------------------------------------------- brand */
  blue: "#0F5CAB",
  blueDeep: "#0B4685",
  blueBright: "#1E88D6",
  cyan: "#3FA9D6",
  cyanLight: "#8ECBF0",

  /* ------------------------------------------------------------- content */
  border: "#E2E8F1",
  borderStrong: "#CFDAE8",
  text: "#14212F",
  muted: "#5B6B80",
  onDark: "#FFFFFF",
  onDarkMuted: "rgba(255, 255, 255, 0.70)",
  onDarkFaint: "rgba(255, 255, 255, 0.46)",

  /* ---------------------------------------------------------- elevation */
  /** The login/forgot card. Short, layered, no halo. */
  cardShadow:
    "0 1px 2px rgba(11, 27, 46, 0.05), 0 10px 28px rgba(11, 27, 46, 0.08), 0 32px 64px rgba(11, 27, 46, 0.10)",
  ctaShadow: "0 1px 2px rgba(11, 27, 46, 0.14), 0 8px 20px rgba(15, 92, 171, 0.28)",
  ctaShadowHover: "0 2px 4px rgba(11, 27, 46, 0.16), 0 12px 26px rgba(15, 92, 171, 0.34)",

  /* ------------------------------------------------------------ gradient */
  /** Primary call to action. GT blue family — never the reference's gold. */
  ctaGradient: "linear-gradient(135deg, #0F5CAB 0%, #1878C4 52%, #1E9AD6 100%)",
  ctaGradientHover: "linear-gradient(135deg, #1266BB 0%, #1B84D2 52%, #29A6E0 100%)",

  /* -------------------------------------------------------- proportions */
  /** Card width, inside the 430–470px band the design calls for. */
  cardWidth: 452,
  /** Input and CTA height. */
  fieldHeight: 52,
  ctaHeight: 52,
} as const;

/** Translucent "glass" fill used by the feature rows on the branding panel. */
export const authGlass = {
  background: "rgba(255, 255, 255, 0.045)",
  border: "1px solid rgba(255, 255, 255, 0.09)",
} as const;
